import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import api, { BACKEND_URL } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Undo, RefreshCw, Plus, Users, User } from 'lucide-react';
import { Input } from '@/components/ui/input';

/* Stat-capture flash (build 216).
   ─────────────────────────────────────────────────────────────────
   Pulses a brand-orange box-shadow around the tapped pill for
   ~320ms so coaches get a non-verbal "stat recorded" confirmation
   without a toast. The class is added imperatively (rather than
   via React state) so re-tapping the same button restarts the
   keyframes — CSS animations don't replay when the same class is
   left on the element, so we remove it on `animationend` and let
   the next tap re-add it. */
function flashStatButton(target) {
  if (!target || !target.classList) return;
  target.classList.remove('stat-flash');
  // Force a reflow so the browser registers the removal before we
  // re-add the class (otherwise the second tap doesn't re-animate).
  // eslint-disable-next-line no-unused-expressions
  target.offsetWidth;
  target.classList.add('stat-flash');
  const onEnd = () => {
    target.classList.remove('stat-flash');
    target.removeEventListener('animationend', onEnd);
  };
  target.addEventListener('animationend', onEnd);
}

export default function InGame() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  // Mode comes from the GameSetupWizard via router state on initial entry.
  // On resume (no router state), it's derived from the persisted game doc
  // once it loads — see the useMemo below.
  const stateMode = routerLocation.state?.mode || null;
  const stateFocusPlayerId = routerLocation.state?.focusPlayerId || null;
  const [game, setGame] = useState(null);
  const mode = useMemo(() => {
    if (stateMode) return stateMode;
    if (!game) return 'MY_TEAM';
    // Derive from persisted fields on the game doc.
    if (game.tracking_mode) return game.tracking_mode;
    if (game.away_tracking_mode === 'players') return 'BOTH_TEAMS';
    if (game.focus_player_id) return 'MY_PLAYER';
    return 'MY_TEAM';
  }, [stateMode, game]);
  const focusPlayerId = useMemo(() => {
    if (mode !== 'MY_PLAYER') return null;
    return stateFocusPlayerId || game?.focus_player_id || null;
  }, [mode, stateFocusPlayerId, game]);
  const [teamName, setTeamName] = useState('');
  const [roster, setRoster] = useState({ home: [], away: [] });
  const [events, setEvents] = useState([]);
  const [trackingMode, setTrackingMode] = useState('individual'); // 'team' or 'individual'
  
  // Modal states
  const [showAthleteModal, setShowAthleteModal] = useState(false);
  const [showAssistModal, setShowAssistModal] = useState(false);
  const [showFTSetupModal, setShowFTSetupModal] = useState(false);
  const [showFTRecordModal, setShowFTRecordModal] = useState(false);
  const [showReboundModal, setShowReboundModal] = useState(false);
  const [reboundView, setReboundView] = useState('options'); // 'options' | 'players'
  const [showReboundTypeModal, setShowReboundTypeModal] = useState(false);
  const [showSubstitutionModal, setShowSubstitutionModal] = useState(false);
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [substitutionSide, setSubstitutionSide] = useState(null);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [newPlayerJersey, setNewPlayerJersey] = useState('');

  // End-game / exit confirmation sheet
  const [showExitSheet, setShowExitSheet] = useState(false);
  const [exiting, setExiting] = useState(false);
  
  // For tracking the shooter after selection (used for assist flow)
  const [pendingShooter, setPendingShooter] = useState(null);
  
  // Pending action states
  const [pendingAction, setPendingAction] = useState(null);
  const [ftType, setFtType] = useState(null);
  const [ftAttempts, setFtAttempts] = useState([]);
  const [currentFTIndex, setCurrentFTIndex] = useState(0);
  const [ftNeedsRebound, setFtNeedsRebound] = useState(false);
  const [pendingRebound, setPendingRebound] = useState(null);

  // BOTH_TEAMS attribution modal — shown after every home-side team-pill
  // tap so the user can attribute the stat to a specific player. Stash
  // the event params in `pendingAttribution`; on player select we
  // create the event with that player_id and (for misses) chain into
  // the rebound modal.
  const [showAttributeModal, setShowAttributeModal] = useState(false);
  const [pendingAttribution, setPendingAttribution] = useState(null);
  // Shape: { side, eventType, subType, points, openReboundOnMiss }
  
  const ws = useRef(null);

  useEffect(() => {
    fetchGame();
    fetchRoster();
    fetchEvents();
    connectWebSocket();

    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [gameId]);

  /* Back-navigation guard.
     Push a phantom history entry on mount so the first browser-back press
     fires our popstate handler (and re-pushes to keep guarding) instead of
     leaving the in-progress game. The user confirms via the exit Sheet. */
  useEffect(() => {
    window.history.pushState({ inGame: true }, '');
    const onPop = () => {
      setShowExitSheet(true);
      // re-push so the next back press also prompts
      window.history.pushState({ inGame: true }, '');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const connectWebSocket = () => {
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    ws.current = new WebSocket(`${wsUrl}/ws/${gameId}`);

    ws.current.onmessage = (event) => {
      const message = JSON.parse(event.data);
      if (message.type === 'score_update' || message.type === 'game_update') {
        setGame(message.data);
      } else if (message.type === 'event_created') {
        fetchEvents();
      }
    };

    ws.current.onerror = () => {
      console.log('WebSocket connection error');
    };
  };

  const fetchGame = async () => {
    try {
      const response = await api.get(`/games/${gameId}`);
      setGame(response.data);
      // Persistent state — flip scheduled → in_progress the moment we
      // open the tracker, so the game survives an exit/crash and can be
      // resumed exactly where it left off.
      if (response.data?.status === 'scheduled') {
        api.patch(`/games/${gameId}`, { status: 'in_progress' }).catch(() => {});
      }
      // Fetch team name
      if (response.data.team_id) {
        const teamResp = await api.get(`/teams/${response.data.team_id}`);
        setTeamName(teamResp.data.name);
      }
    } catch (error) {
      toast.error('Failed to load game');
    }
  };

  const fetchRoster = async () => {
    try {
      const [homeResp, awayResp] = await Promise.all([
        api.get(`/games/${gameId}/roster`, { params: { side: 'home' } }),
        api.get(`/games/${gameId}/roster`, { params: { side: 'away' } })
      ]);
      setRoster({ home: homeResp.data, away: awayResp.data });
    } catch (error) {
      console.error('Failed to load roster:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await api.get(`/games/${gameId}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to load events:', error);
    }
  };

  const createEvent = async (eventData) => {
    try {
      await api.post(`/games/${gameId}/events`, eventData);
      await fetchGame();
      await fetchEvents();
    } catch (error) {
      toast.error('Failed to record event');
    }
  };

  // Initiate stat recording - opens athlete selection or records team stat
  const handleStatButton = (side, eventType, subType, points = 0) => {
    setPendingAction({
      side,
      eventType,
      subType,
      points,
      isMiss: eventType === 'shot_miss'
    });
    
    // If team-only mode, record team stat directly
    if (trackingMode === 'team') {
      recordTeamStat(side, eventType, subType, points);
      return;
    }

    // Favorite/focus player shortcut — auto-attribute to the favorite player on the
    // user's own side (home). Skips the "which player?" confirmation modal entirely.
    if (focusPlayerId && side === 'home') {
      const fav = roster.home.find(p => p.player_id === focusPlayerId);
      if (fav) {
        recordForFocusPlayer(side, eventType, subType, points, fav);
        return;
      }
    }

    setShowAthleteModal(true);
  };

  // Record a stat directly attributed to the favorite/focus player, bypassing the selector.
  // Follow-on workflows (assist on made shot, rebound on miss) are preserved.
  const recordForFocusPlayer = async (side, eventType, subType, points, player) => {
    await createEvent({
      period: game.current_period,
      game_clock_time: '12:00',
      side,
      team_id: game.team_id,
      player_id: player.player_id,
      event_type: eventType,
      sub_type: subType,
      points
    });

    if (eventType === 'shot_make' && (subType === '2pt' || subType === '3pt')) {
      setPendingShooter({
        athleteId: player.player_id,
        athleteName: player.player_name || player.name,
        jerseyNumber: player.jersey_number,
        side
      });
      setShowAssistModal(true);
      return;
    }

    if (eventType === 'shot_miss') {
      setPendingRebound({ side, missType: subType });
      setShowReboundModal(true);
    }

    setPendingAction(null);
  };

  // Record a team stat (no player assigned)
  const recordTeamStat = async (side, eventType, subType, points) => {
    const action = { side, eventType, subType, points, isMiss: eventType === 'shot_miss' };
    
    await createEvent({
      period: game.current_period,
      game_clock_time: '12:00',
      side: side,
      team_id: game.team_id,
      player_id: null,
      event_type: eventType,
      sub_type: subType,
      points: points
    });

    const statName = getStatName(eventType, subType);
    const teamDisplayName = side === 'home' ? (game.is_home ? teamName : game.opponent_name) : (game.is_home ? game.opponent_name : teamName);

    // If it was a miss, show rebound dialog
    if (action.isMiss) {
      setPendingRebound({ side: side, missType: subType });
      setShowReboundModal(true);
    }

    setPendingAction(null);
  };

  // Handle athlete selection for regular stats
  const handleAthleteSelected = async (athleteId, athleteName, jerseyNumber) => {
    const action = pendingAction;
    setShowAthleteModal(false);

    // Record the stat
    await createEvent({
      period: game.current_period,
      game_clock_time: '12:00',
      side: action.side,
      team_id: game.team_id,
      player_id: athleteId,
      event_type: action.eventType,
      sub_type: action.subType,
      points: action.points
    });

    const statName = getStatName(action.eventType, action.subType);

    // If it was a MADE shot (2pt or 3pt), ask for assist
    if (action.eventType === 'shot_make' && (action.subType === '2pt' || action.subType === '3pt')) {
      setPendingShooter({ athleteId, athleteName, jerseyNumber, side: action.side });
      setShowAssistModal(true);
      return; // Don't clear pendingAction yet - we'll do it after assist selection
    }

    // If it was a miss, show rebound dialog
    if (action.isMiss) {
      setPendingRebound({ side: action.side, missType: action.subType });
      setShowReboundModal(true);
    }

    setPendingAction(null);
  };

  // Handle assist selection after a made shot
  const handleAssistSelected = async (athleteId, athleteName, jerseyNumber) => {
    setShowAssistModal(false);
    
    if (athleteId && athleteId !== 'no_assist') {
      // Record the assist
      await createEvent({
        period: game.current_period,
        game_clock_time: '12:00',
        side: pendingShooter.side,
        team_id: game.team_id,
        player_id: athleteId,
        event_type: 'assist',
        sub_type: null,
        points: 0
      });
    }
    
    // Clear states
    setPendingShooter(null);
    setPendingAction(null);
  };

  // Free throw workflow
  const handleFTButton = (side) => {
    setPendingAction({ side });
    setShowFTSetupModal(true);
  };

  const handleFTTypeSelected = (type) => {
    setFtType(type);
    setFtAttempts([]);
    setCurrentFTIndex(0);
    setShowFTSetupModal(false);
    setShowFTRecordModal(true);
  };

  const handleFTAttempt = (isMake) => {
    const newAttempts = [...ftAttempts, { isMake, index: currentFTIndex }];
    setFtAttempts(newAttempts);

    // Determine if we need more attempts
    const maxAttempts = ftType === '3FT' ? 3 : ftType === '2FT' ? 2 : 2;
    const isOneAndOne = ftType === '1AND1';

    // Check if this is the final FT and it was a miss (triggers rebound)
    const isLastAttempt = (isOneAndOne && currentFTIndex === 0 && !isMake) || (currentFTIndex + 1 >= maxAttempts);
    const lastAttemptWasMiss = !isMake;

    if (isOneAndOne && currentFTIndex === 0 && !isMake) {
      // 1-and-1: miss on first attempt, done - trigger rebound
      setShowFTRecordModal(false);
      setFtAttempts(newAttempts);
      setFtNeedsRebound(true); // Mark that we need rebound after player selection
      if (trackingMode === 'team') {
        handleFTTeamRecordWithRebound(newAttempts);
      } else {
        setShowAthleteModal(true);
      }
    } else if (currentFTIndex + 1 < maxAttempts) {
      // More attempts needed
      setCurrentFTIndex(currentFTIndex + 1);
    } else {
      // All attempts recorded, select athlete
      setShowFTRecordModal(false);
      // Check if final FT was a miss - triggers rebound
      setFtNeedsRebound(lastAttemptWasMiss);
      if (trackingMode === 'team') {
        if (lastAttemptWasMiss) {
          handleFTTeamRecordWithRebound(newAttempts);
        } else {
          handleFTTeamRecord(newAttempts);
        }
      } else {
        setShowAthleteModal(true);
      }
    }
  };

  // Record FT as team stat
  const handleFTTeamRecord = async (attempts) => {
    for (const attempt of attempts) {
      await createEvent({
        period: game.current_period,
        game_clock_time: '12:00',
        side: pendingAction.side,
        team_id: game.team_id,
        player_id: null,
        event_type: attempt.isMake ? 'shot_make' : 'shot_miss',
        sub_type: 'ft',
        points: attempt.isMake ? 1 : 0
      });
    }

    const makes = attempts.filter(a => a.isMake).length;
    const teamDisplayName = pendingAction.side === 'home' ? (game.is_home ? teamName : game.opponent_name) : (game.is_home ? game.opponent_name : teamName);

    // Reset FT state
    setFtType(null);
    setFtAttempts([]);
    setCurrentFTIndex(0);
    setFtNeedsRebound(false);
    setPendingAction(null);
  };

  // Record FT as team stat with rebound workflow
  const handleFTTeamRecordWithRebound = async (attempts) => {
    for (const attempt of attempts) {
      await createEvent({
        period: game.current_period,
        game_clock_time: '12:00',
        side: pendingAction.side,
        team_id: game.team_id,
        player_id: null,
        event_type: attempt.isMake ? 'shot_make' : 'shot_miss',
        sub_type: 'ft',
        points: attempt.isMake ? 1 : 0
      });
    }

    const makes = attempts.filter(a => a.isMake).length;
    const teamDisplayName = pendingAction.side === 'home' ? (game.is_home ? teamName : game.opponent_name) : (game.is_home ? game.opponent_name : teamName);

    // Trigger rebound workflow
    setPendingRebound({ side: pendingAction.side, missType: 'ft' });
    setShowReboundModal(true);

    // Reset FT state but keep pendingAction for rebound context
    setFtType(null);
    setFtAttempts([]);
    setCurrentFTIndex(0);
    setFtNeedsRebound(false);
  };

  const handleFTAthleteSelected = async (athleteId, athleteName, jerseyNumber) => {
    setShowAthleteModal(false);

    // Record each FT attempt
    for (const attempt of ftAttempts) {
      await createEvent({
        period: game.current_period,
        game_clock_time: '12:00',
        side: pendingAction.side,
        team_id: game.team_id,
        player_id: athleteId,
        event_type: attempt.isMake ? 'shot_make' : 'shot_miss',
        sub_type: 'ft',
        points: attempt.isMake ? 1 : 0
      });
    }

    const makes = ftAttempts.filter(a => a.isMake).length;

    // Check if we need to trigger rebound workflow (final FT was a miss)
    const needsRebound = ftNeedsRebound;
    const actionSide = pendingAction.side;

    // Reset FT state
    setFtType(null);
    setFtAttempts([]);
    setCurrentFTIndex(0);
    setFtNeedsRebound(false);

    if (needsRebound) {
      // Trigger rebound workflow
      setPendingRebound({ side: actionSide, missType: 'ft' });
      setShowReboundModal(true);
    } else {
      setPendingAction(null);
    }
  };

  // Rebound workflow
  const handleReboundAthleteSelected = async (athleteId, athleteName, jerseyNumber, selectedSide) => {
    setShowReboundModal(false);
    
    if (athleteId === 'skip' || athleteId === 'no_rebound') {
      // Skip rebound tracking or explicitly no rebound
      setPendingRebound(null);
      setPendingAction(null);
      return;
    }
    
    // Determine if this is offensive or defensive rebound based on which team grabbed it
    const missingSide = pendingRebound.side;
    const reboundSide = selectedSide;
    const isOffensive = missingSide === reboundSide;
    const reboundType = isOffensive ? 'offensive' : 'defensive';
    
    await createEvent({
      period: game.current_period,
      game_clock_time: '12:00',
      side: reboundSide,
      team_id: game.team_id,
      player_id: athleteId,
      event_type: 'rebound',
      sub_type: reboundType,
      points: 0
    });
    
    setPendingRebound(null);
    setPendingAction(null);
  };

  const handleReboundType = async (reboundType) => {
    setShowReboundTypeModal(false);

    await createEvent({
      period: game.current_period,
      game_clock_time: '12:00',
      side: pendingRebound.side,
      team_id: game.team_id,
      player_id: pendingRebound.athleteId,
      event_type: 'rebound',
      sub_type: reboundType,
      points: 0
    });

    setPendingRebound(null);
    setPendingAction(null);
  };

  const getStatName = (eventType, subType) => {
    if (eventType === 'shot_make') {
      return subType === '2pt' ? '2-pointer' : subType === '3pt' ? '3-pointer' : 'Free throw';
    }
    if (eventType === 'shot_miss') {
      return subType === '2pt' ? '2PT miss' : '3PT miss';
    }
    const names = {
      assist: 'Assist',
      steal: 'Steal',
      block: 'Block',
      deflection: 'Deflection',
      turnover: 'Turnover',
      foul: 'Foul'
    };
    return names[eventType] || eventType;
  };

  const getModalTitle = () => {
    if (!pendingAction) return '';
    
    if (ftAttempts.length > 0) {
      return 'Who shot the free throws?';
    }

    const { eventType, subType } = pendingAction;
    if (eventType === 'shot_make') {
      return subType === '2pt' ? 'Who made the 2-pointer?' : 'Who made the 3-pointer?';
    }
    if (eventType === 'shot_miss') {
      return subType === '2pt' ? 'Who missed the 2-pointer?' : 'Who missed the 3-pointer?';
    }
    const questions = {
      assist: 'Who got the assist?',
      steal: 'Who got the steal?',
      block: 'Who got the block?',
      deflection: 'Who got the deflection?',
      turnover: 'Who had the turnover?',
      foul: 'Who committed the foul?'
    };
    return questions[eventType] || 'Select athlete';
  };

  // Calculate player fouls from events
  const getPlayerFouls = (playerId, side) => {
    return events.filter(e => 
      e.player_id === playerId && 
      e.side === side && 
      e.event_type === 'foul'
    ).length;
  };

  // Calculate team fouls for current half
  const getTeamFoulsForHalf = (side) => {
    const format = game?.period_format || 'quarters';
    const currentPeriod = game?.current_period || 1;
    
    // Determine which half we're in
    let periodsInCurrentHalf = [];
    if (format === 'quarters') {
      if (currentPeriod <= 2) {
        periodsInCurrentHalf = [1, 2];
      } else {
        periodsInCurrentHalf = [3, 4, 5]; // Include OT in second half
      }
    } else {
      // Halves format
      periodsInCurrentHalf = [currentPeriod];
    }
    
    return events.filter(e => 
      e.side === side && 
      e.event_type === 'foul' &&
      periodsInCurrentHalf.includes(e.period)
    ).length;
  };

  // Get foul color class based on count
  const getFoulColorClass = (fouls) => {
    if (fouls >= 5) return 'bg-red-500 text-white';
    if (fouls >= 4) return 'bg-orange-500 text-white';
    if (fouls >= 3) return 'bg-yellow-400 text-slate-900';
    return 'bg-slate-200 text-slate-600';
  };

  const handleUndo = async () => {
    if (events.length === 0) return;
    
    try {
      await api.delete(`/events/${events[0].event_id}`);
      await fetchGame();
      await fetchEvents();
      toast.success('Last action undone');
    } catch (error) {
      toast.error('Failed to undo');
    }
  };

  const handleEndGame = () => {
    // Open the bottom-sheet modal (Save & Exit / Discard & Exit).
    setShowExitSheet(true);
  };

  const handleSaveAndExit = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      toast.loading('Saving final stats…', { id: 'end-game' });
      await api.post(`/games/${gameId}/end`);
      toast.success('Game saved.', { id: 'end-game' });
      setShowExitSheet(false);
      navigate('/games', { replace: true });
    } catch (error) {
      toast.error('Failed to save game', { id: 'end-game' });
      setExiting(false);
    }
  };

  // Pause = leave the game in-progress without finalizing.
  // Stats are already persisted live as events are tapped, so all we
  // need to do is flip the status to `in_progress` (so it shows up in
  // /games as a LIVE row with a Resume pill) and navigate away.
  const handlePauseAndExit = async () => {
    if (exiting) return;
    setShowExitSheet(false);
    try {
      // Only bump status if not already final/locked (defensive).
      if (game && game.status !== 'final' && game.status !== 'locked') {
        await api.patch(`/games/${gameId}`, { status: 'in_progress' });
      }
    } catch {
      // Non-fatal — events are already saved.
    }
    navigate('/games', { replace: true });
  };

  const handleDiscardAndExit = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      toast.loading('Discarding game…', { id: 'end-game' });
      await api.delete(`/games/${gameId}`);
      toast.success('Game discarded.', { id: 'end-game' });
      setShowExitSheet(false);
      navigate('/games', { replace: true });
    } catch (error) {
      toast.error('Failed to discard game', { id: 'end-game' });
      setExiting(false);
    }
  };

  const toggleOnCourt = async (rosterId, currentStatus) => {
    try {
      await api.patch(`/games/${gameId}/roster/${rosterId}`, {
        on_court: !currentStatus
      });
      fetchRoster();
    } catch (error) {
      toast.error('Failed to update roster');
    }
  };

  // Substitution handlers
  const handleOpenSubstitution = (side) => {
    setSubstitutionSide(side);
    setShowSubstitutionModal(true);
  };

  const handleSubstitution = async (playerIn, playerOut) => {
    // Check 5 player limit when subbing in
    if (playerIn && !playerOut) {
      const currentOnCourt = roster[substitutionSide]?.filter(p => p.on_court).length || 0;
      if (currentOnCourt >= 5) {
        toast.error('Maximum 5 players on court. Sub out a player first.');
        return;
      }
    }
    
    try {
      // Sub out the current player
      if (playerOut) {
        await api.patch(`/games/${gameId}/roster/${playerOut.roster_id}`, {
          on_court: false
        });
      }
      // Sub in the new player
      if (playerIn) {
        await api.patch(`/games/${gameId}/roster/${playerIn.roster_id}`, {
          on_court: true
        });
      }
      await fetchRoster();
    } catch (error) {
      toast.error('Failed to make substitution');
    }
  };

  const handleAddPlayerToRoster = async () => {
    if (!newPlayerName.trim()) {
      toast.error('Please enter player name');
      return;
    }
    
    try {
      // First create the player profile
      const playerResponse = await api.post(`/teams/${game.team_id}/players`, {
        name: newPlayerName.trim(),
        jersey_number: newPlayerJersey.trim() || null
      });
      
      // Then add to game roster
      await api.post(`/games/${gameId}/roster`, {
        player_id: playerResponse.data.player_id,
        side: substitutionSide,
        is_starter: false,
        is_active: true,
        on_court: false
      });
      
      await fetchRoster();
      toast.success(`${newPlayerName} added to roster`);
      setNewPlayerName('');
      setNewPlayerJersey('');
      setShowAddPlayerModal(false);
    } catch (error) {
      toast.error('Failed to add player');
      console.error(error);
    }
  };

  if (!game) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin h-12 w-12 border-4 border-slate-900 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Helper to get player initials
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Helper to get player name from roster by player_id
  const getPlayerName = (playerId, side) => {
    if (!playerId) return null;
    const allPlayers = [...roster.home, ...roster.away];
    const player = allPlayers.find(p => p.player_id === playerId);
    return player ? player.player_name : null;
  };

  const TeamPanel = ({ side, score, displayName }) => {
    const onCourt = roster[side].filter(p => p.on_court);
    const teamFouls = getTeamFoulsForHalf(side);
    const format = game?.period_format || 'quarters';
    const currentPeriod = game?.current_period || 1;
    
    // Determine which half we're in for display
    let halfLabel = '';
    if (format === 'quarters') {
      halfLabel = currentPeriod <= 2 ? '1st Half' : '2nd Half';
    } else {
      halfLabel = `Half ${currentPeriod}`;
    }

    return (
      <div className="bg-white border-2 border-slate-200 rounded-lg p-3 sm:p-4">
        <div className="flex items-center justify-between mb-2 gap-2">
          <div className="min-w-0">
            <h3 className="text-base sm:text-xl font-bold font-heading uppercase tracking-tight text-slate-900 truncate">
              {displayName}
            </h3>
            <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-slate-600">
              <span>On: {onCourt.length}/5</span>
              <span className="text-slate-300">|</span>
              <span className={`font-bold ${teamFouls >= 7 ? 'text-red-600' : teamFouls >= 5 ? 'text-orange-600' : 'text-slate-600'}`}>
                Fouls: {teamFouls} <span className="hidden sm:inline">({halfLabel})</span>
              </span>
            </div>
          </div>
          <div className="text-4xl sm:text-5xl font-bold text-slate-900 tabular-nums score-update shrink-0">
            {score}
          </div>
        </div>

        {/* On Court Players Display with Foul Indicators */}
        {onCourt.length > 0 && (
          <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3">
            {onCourt.map((player) => {
              const fouls = getPlayerFouls(player.player_id, side);
              const foulColorClass = getFoulColorClass(fouls);
              return (
                <div
                  key={player.player_id}
                  className="flex items-center bg-slate-100 rounded-full px-2 sm:px-3 py-0.5 sm:py-1"
                  title={`${player.player_name} - ${fouls} fouls`}
                >
                  <span className="font-bold text-xs sm:text-sm text-slate-700">
                    #{player.jersey_number || '?'}
                  </span>
                  <span className="ml-1 text-xs sm:text-sm text-slate-600">
                    {getInitials(player.player_name)}
                  </span>
                  {fouls > 0 && (
                    <span className={`ml-1.5 sm:ml-2 text-[10px] sm:text-xs font-bold px-1 sm:px-1.5 py-0.5 rounded ${foulColorClass}`}>
                      {fouls}F
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* 3×4 STAT GRID — uniform pill buttons */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {/* Row 1 — makes */}
          <Button
            onClick={() => handleStatButton(side, 'shot_make', '2pt', 2)}
            data-testid={`${side}-2pt-make`}
            style={{ backgroundColor: '#16A085' }}
            className="h-14 sm:h-[72px] w-full hover:opacity-90 text-white shadow-md hover:shadow-lg font-bold text-sm sm:text-base rounded-full px-1"
          >
            +2 MAKE
          </Button>
          <Button
            onClick={() => handleStatButton(side, 'shot_make', 'ft', 1)}
            data-testid={`${side}-ft-make`}
            style={{ backgroundColor: '#16A085' }}
            className="h-14 sm:h-[72px] w-full hover:opacity-90 text-white shadow-md hover:shadow-lg font-bold text-sm sm:text-base rounded-full px-1"
          >
            FT MAKE
          </Button>
          <Button
            onClick={() => handleStatButton(side, 'shot_make', '3pt', 3)}
            data-testid={`${side}-3pt-make`}
            style={{ backgroundColor: '#16A085' }}
            className="h-14 sm:h-[72px] w-full hover:opacity-90 text-white shadow-md hover:shadow-lg font-bold text-sm sm:text-base rounded-full px-1"
          >
            +3 MAKE
          </Button>

          {/* Row 2 — misses */}
          <Button
            onClick={() => handleStatButton(side, 'shot_miss', '2pt')}
            data-testid={`${side}-2pt-miss`}
            style={{ backgroundColor: '#C0392B' }}
            className="h-14 sm:h-[72px] w-full hover:opacity-90 text-white shadow-md hover:shadow-lg font-bold text-sm sm:text-base rounded-full px-1"
          >
            +2 MISS
          </Button>
          <Button
            onClick={() => handleStatButton(side, 'shot_miss', 'ft')}
            data-testid={`${side}-ft-miss`}
            style={{ backgroundColor: '#C0392B' }}
            className="h-14 sm:h-[72px] w-full hover:opacity-90 text-white shadow-md hover:shadow-lg font-bold text-sm sm:text-base rounded-full px-1"
          >
            FT MISS
          </Button>
          <Button
            onClick={() => handleStatButton(side, 'shot_miss', '3pt')}
            data-testid={`${side}-3pt-miss`}
            style={{ backgroundColor: '#C0392B' }}
            className="h-14 sm:h-[72px] w-full hover:opacity-90 text-white shadow-md hover:shadow-lg font-bold text-sm sm:text-base rounded-full px-1"
          >
            +3 MISS
          </Button>

          {/* Row 3 — AST / STL / BLK */}
          <Button
            onClick={() => handleStatButton(side, 'assist', null)}
            data-testid={`${side}-assist`}
            style={{ backgroundColor: '#13314b' }}
            className="h-14 sm:h-[72px] w-full hover:opacity-90 text-white shadow-md hover:shadow-lg font-bold text-sm sm:text-base rounded-full px-1"
          >
            AST
          </Button>
          <Button
            onClick={() => handleStatButton(side, 'steal', null)}
            data-testid={`${side}-steal`}
            style={{ backgroundColor: '#13314b' }}
            className="h-14 sm:h-[72px] w-full hover:opacity-90 text-white shadow-md hover:shadow-lg font-bold text-sm sm:text-base rounded-full px-1"
          >
            STL
          </Button>
          <Button
            onClick={() => handleStatButton(side, 'block', null)}
            data-testid={`${side}-block`}
            style={{ backgroundColor: '#13314b' }}
            className="h-14 sm:h-[72px] w-full hover:opacity-90 text-white shadow-md hover:shadow-lg font-bold text-sm sm:text-base rounded-full px-1"
          >
            BLK
          </Button>

          {/* Row 4 — DEF / TO / FOUL */}
          <Button
            onClick={() => handleStatButton(side, 'deflection', null)}
            data-testid={`${side}-deflection`}
            style={{ backgroundColor: '#13314b' }}
            className="h-14 sm:h-[72px] w-full hover:opacity-90 text-white shadow-md hover:shadow-lg font-bold text-sm sm:text-base rounded-full px-1"
          >
            DEF
          </Button>
          <Button
            onClick={() => handleStatButton(side, 'turnover', null)}
            data-testid={`${side}-turnover`}
            style={{ backgroundColor: '#ffa133' }}
            className="h-14 sm:h-[72px] w-full hover:opacity-90 text-white shadow-md hover:shadow-lg font-bold text-sm sm:text-base rounded-full px-1"
          >
            TO
          </Button>
          <Button
            onClick={() => handleStatButton(side, 'foul', null)}
            data-testid={`${side}-foul`}
            style={{ backgroundColor: '#ffa133' }}
            className="h-14 sm:h-[72px] w-full hover:opacity-90 text-white shadow-md hover:shadow-lg font-bold text-sm sm:text-base rounded-full px-1"
          >
            FOUL
          </Button>
        </div>
      </div>
    );
  };

  /* ──────────────────────────────────────────────────────────
     MY PLAYER SCORING PANEL
     Used only when `focusPlayerId` is set. Replaces both home
     and away TeamPanels with a personal-stats UI: 3×4 pill grid
     up top, six live-calculated stat tiles below.
  ────────────────────────────────────────────────────────── */
  const recordMyPlayerEvent = async (eventType, subType, points) => {
    await createEvent({
      period: game.current_period,
      game_clock_time: '12:00',
      side: 'home',
      team_id: game.team_id,
      player_id: focusPlayerId,
      event_type: eventType,
      sub_type: subType,
      points,
    });
  };

  const adjustGameScore = async (which, delta) => {
    const field = which === 'team' ? (game.is_home ? 'home_score' : 'away_score')
                                   : (game.is_home ? 'away_score' : 'home_score');
    const next = Math.max(0, (game[field] || 0) + delta);
    try {
      await api.patch(`/games/${gameId}`, { [field]: next });
      await fetchGame();
    } catch {
      toast.error('Failed to adjust score');
    }
  };

  /**
   * Set the active period for stat capture. Used by the quarter selector
   * in the team / player score headers. Quarters 1–4 plus OT (period 5).
   * Optimistically updates local game state so the selected pill flips
   * without waiting for the round-trip, then patches the server.
   */
  const setGamePeriod = async (period) => {
    if (!game || game.current_period === period) return;
    const prev = game.current_period;
    setGame(g => g ? { ...g, current_period: period } : g);
    try {
      await api.patch(`/games/${gameId}`, { current_period: period });
    } catch {
      setGame(g => g ? { ...g, current_period: prev } : g);
      toast.error('Failed to change quarter');
    }
  };

  /**
   * QuarterSelector (build 200)
   * Single tap-to-cycle pill: Q1 → Q2 → Q3 → Q4 → OT → Q1.
   * Solid navy fill, white text. Sits compactly between the home
   * and away score blocks in both score-header layouts.
   * For halves format the cycle is H1 → H2 → OT → H1.
   */
  const QuarterSelector = () => {
    if (!game) return null;
    const fmt = game.period_format || 'quarters';
    const p = game.current_period || 1;
    const regulationCount = fmt === 'halves' ? 2 : 4;
    const isOT = p > regulationCount;

    let label;
    if (isOT) label = 'OT';
    else if (fmt === 'halves') label = p === 1 ? 'H1' : 'H2';
    else label = `Q${p}`;

    const cycle = () => {
      // Q1..Qn → next period; OT (any period > regulation) → wrap to 1.
      if (isOT) {
        setGamePeriod(1);
      } else if (p >= regulationCount) {
        setGamePeriod(regulationCount + 1); // jump to OT
      } else {
        setGamePeriod(p + 1);
      }
    };

    return (
      <button
        type="button"
        onClick={cycle}
        data-testid="quarter-selector"
        className="active:scale-95 transition-transform self-center"
        style={{
          // Build 216: pill-shaped quarter indicator. Brand yellow
          // background + navy text, large bold Barlow Condensed so
          // the period is readable at a glance during a fast-paced
          // game (previously navy pill with 13px white text — too
          // easy to miss when the coach is tracking shots).
          minWidth: 64,
          padding: '10px 20px',
          background: '#ffa133',
          border: 'none',
          borderRadius: 999,
          fontSize: 22,
          fontWeight: 800,
          color: '#0B1824',
          letterSpacing: '0.06em',
          fontFamily: "'Barlow Condensed', sans-serif",
          lineHeight: 1,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        {label}
      </button>
    );
  };

  /* ──────────────────────────────────────────────────────────
     Team-pill capture path (used by My Team & Both Teams modes)
     - In BOTH_TEAMS mode + home side, every stat pops a "Who
       completed it?" attribution modal that drops from the top
       so the event ends up tagged with the player_id (flows to
       box score + game summary).
     - In other modes / for the away side, falls back to the legacy
       team-level capture (no player attribution).
     - Misses still chain into the rebound modal AFTER attribution.
  ────────────────────────────────────────────────────────── */
  const recordTeamPill = (side, eventType, subType, points, openReboundOnMiss = false) => {
    if (mode === 'BOTH_TEAMS' && side === 'home') {
      setPendingAttribution({ side, eventType, subType, points, openReboundOnMiss });
      setShowAttributeModal(true);
      return;
    }
    // For misses that chain into a rebound, OPEN THE REBOUND MODAL
    // SYNCHRONOUSLY first — the user's next decision is the rebounder,
    // not waiting on a network round-trip. The miss event fires in the
    // background; if it fails, an error toast surfaces.
    if (openReboundOnMiss && eventType === 'shot_miss') {
      setPendingRebound({ side, missType: subType });
      setShowReboundModal(true);
    }
    createEvent({
      period: game.current_period,
      game_clock_time: '12:00',
      side,
      team_id: game.team_id,
      player_id: null,
      event_type: eventType,
      sub_type: subType,
      points,
    });
  };

  /* ──────────────────────────────────────────────────────────
     Apply a queued home-side stat to a chosen player.
     Closes the attribution modal, records the event with the
     player_id, and (if the stat was a miss) chains into the
     rebound modal so the rebounder is captured separately.
  ────────────────────────────────────────────────────────── */
  const completeHomeAttribution = (playerId) => {
    const p = pendingAttribution;
    if (!p) { setShowAttributeModal(false); return; }
    setShowAttributeModal(false);
    setPendingAttribution(null);
    // Open rebound modal SYNCHRONOUSLY for misses — instantaneous UX.
    // The createEvent network call runs in the background.
    if (p.openReboundOnMiss && p.eventType === 'shot_miss') {
      setPendingRebound({ side: p.side, missType: p.subType });
      setShowReboundModal(true);
    }
    createEvent({
      period: game.current_period,
      game_clock_time: '12:00',
      side: p.side,
      team_id: game.team_id,
      player_id: playerId,
      event_type: p.eventType,
      sub_type: p.subType,
      points: p.points,
    });
  };

  /* ──────────────────────────────────────────────────────────
     TEAM SCORING LAYOUT
     Used when `focusPlayerId` is null. Renders:
       • Manual ± score header (white card)
       • One pill grid (My Team mode) or two (Both Teams mode)
       • TF + UNDO footer
  ────────────────────────────────────────────────────────── */
  const TeamScoringLayout = () => {
    const isBothTeams = mode === 'BOTH_TEAMS';

    const homeFouls = roster.home.reduce(
      (n, p) => n + events.filter(e => e.player_id === p.player_id && e.event_type === 'foul').length,
      0
    ) + events.filter(e => e.side === 'home' && !e.player_id && e.event_type === 'foul').length;

    const teamScore = game.is_home ? game.home_score : game.away_score;
    const oppScore = game.is_home ? game.away_score : game.home_score;
    const teamLabel = (teamName || 'My Team').toUpperCase();
    const oppLabel = (game.opponent_name || 'OPPONENT').toUpperCase();

    const periodLabel = (() => {
      const fmt = game.period_format || 'quarters';
      const p = game.current_period || 1;
      if (fmt === 'halves') return p <= 2 ? `Half ${p}` : 'OT';
      return p <= 2 ? '1st Half' : (p <= 4 ? '2nd Half' : 'OT');
    })();

    const Pill = ({ variant, mainText, subText, onClick, testid }) => {
      const styles = {
        make:    { bg: '#e8963a', label: '#0B1824', sub: '#0B1824' },
        miss:    { bg: '#1e3a52', label: '#ffffff', sub: 'rgba(255,255,255,0.8)' },
        neutral: { bg: '#ffffff', label: '#0B1824', sub: '#0B1824', border: '1px solid rgba(0,0,0,0.1)' },
      }[variant];
      // Build 216: every tap pulses a brand-orange box-shadow ring
      // around the button (.stat-flash) so the coach gets a silent
      // "recorded" confirmation. Wrapped here so every callsite —
      // shot makes/misses, AST/STL/BLK/DEF/TO/FOUL — gets it for free.
      const handleClick = (e) => {
        flashStatButton(e.currentTarget);
        onClick?.(e);
      };
      return (
        <button
          onClick={handleClick}
          data-testid={testid}
          className="rounded-full flex flex-col items-center justify-center select-none active:scale-95 transition-transform"
          style={{
            background: styles.bg,
            border: styles.border || 'none',
            fontFamily: "'Barlow Condensed', sans-serif",
            height: 60,
            padding: '4px',
          }}
        >
          {subText ? (
            <>
              <span style={{ fontSize: 22, fontWeight: 800, color: styles.label, lineHeight: 1, letterSpacing: '-0.01em' }}>{mainText}</span>
              <span style={{ fontSize: 9, fontWeight: 700, color: styles.sub, letterSpacing: '0.06em', marginTop: 3 }}>{subText}</span>
            </>
          ) : (
            <span style={{ fontSize: 17, fontWeight: 800, color: styles.label, lineHeight: 1, letterSpacing: '0.04em' }}>{mainText}</span>
          )}
        </button>
      );
    };

    const AdjBtn = ({ onClick, children, testid }) => (
      <button
        onClick={onClick}
        data-testid={testid}
        className="active:scale-90 transition-transform"
        style={{
          width: 26, height: 26, borderRadius: 6,
          border: '1px solid rgba(0,0,0,0.15)', background: '#f8fafc',
          color: 'rgba(0,0,0,0.4)', fontSize: 16, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: "'Barlow Condensed', sans-serif",
        }}
      >
        {children}
      </button>
    );

    /* Single 3×4 pill grid for one side */
    const PillGrid = ({ side, headingLabel, openReboundOnMiss }) => (
      <div data-testid={`team-pill-grid-${side}`} style={{ background: '#fff', borderRadius: 12, padding: 12, border: '1px solid rgba(0,0,0,0.06)' }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: 'rgba(0,0,0,0.45)', letterSpacing: '0.12em', marginBottom: 8, fontFamily: "'Barlow Condensed', sans-serif" }}>
          {headingLabel}
        </div>
        {/* makes */}
        <div className="grid grid-cols-3" style={{ gap: 5, marginBottom: 5 }}>
          <Pill variant="make" mainText="+2" subText="MAKE" testid={`${side}-pill-m2`}  onClick={() => recordTeamPill(side, 'shot_make', '2pt', 2)} />
          <Pill variant="make" mainText="FT" subText="MAKE" testid={`${side}-pill-mft`} onClick={() => recordTeamPill(side, 'shot_make', 'ft', 1)} />
          <Pill variant="make" mainText="+3" subText="MAKE" testid={`${side}-pill-m3`}  onClick={() => recordTeamPill(side, 'shot_make', '3pt', 3)} />
        </div>
        {/* misses */}
        <div className="grid grid-cols-3" style={{ gap: 5, marginBottom: 5 }}>
          <Pill variant="miss" mainText="+2" subText="MISS" testid={`${side}-pill-x2`}  onClick={() => recordTeamPill(side, 'shot_miss', '2pt', 0, openReboundOnMiss)} />
          <Pill variant="miss" mainText="FT" subText="MISS" testid={`${side}-pill-xft`} onClick={() => recordTeamPill(side, 'shot_miss', 'ft', 0, openReboundOnMiss)} />
          <Pill variant="miss" mainText="+3" subText="MISS" testid={`${side}-pill-x3`}  onClick={() => recordTeamPill(side, 'shot_miss', '3pt', 0, openReboundOnMiss)} />
        </div>
        {/* secondary */}
        <div className="grid grid-cols-3" style={{ gap: 5, marginBottom: 5 }}>
          <Pill variant="neutral" mainText="AST" testid={`${side}-pill-ast`} onClick={() => recordTeamPill(side, 'assist', null, 0)} />
          <Pill variant="neutral" mainText="STL" testid={`${side}-pill-stl`} onClick={() => recordTeamPill(side, 'steal', null, 0)} />
          <Pill variant="neutral" mainText="BLK" testid={`${side}-pill-blk`} onClick={() => recordTeamPill(side, 'block', null, 0)} />
        </div>
        {/* negative */}
        <div className="grid grid-cols-3" style={{ gap: 5 }}>
          <Pill variant="neutral" mainText="DEF"  testid={`${side}-pill-def`}  onClick={() => recordTeamPill(side, 'deflection', null, 0)} />
          <Pill variant="neutral" mainText="TO"   testid={`${side}-pill-to`}   onClick={() => recordTeamPill(side, 'turnover', null, 0)} />
          <Pill variant="neutral" mainText="FOUL" testid={`${side}-pill-foul`} onClick={() => recordTeamPill(side, 'foul', null, 0)} />
        </div>
      </div>
    );

    return (
      <div className="rounded-2xl overflow-hidden" data-testid="team-scoring-layout" style={{ background: '#F1F5F9', fontFamily: "'Barlow Condensed', sans-serif", border: '1px solid rgba(0,0,0,0.08)' }}>
        {/* Score Header — manual ± */}
        <div className="flex items-start justify-between" style={{ padding: '10px 16px', background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(0,0,0,0.4)' }}>{teamLabel}</div>
            <div className="flex items-center" style={{ gap: 5, marginTop: 2 }}>
              <AdjBtn onClick={() => adjustGameScore('team', -1)} testid="team-mode-team-score-down">−</AdjBtn>
              <span style={{ fontSize: 34, fontWeight: 800, color: '#0B1824', lineHeight: 1, letterSpacing: '-1px', minWidth: 36, textAlign: 'center' }} data-testid="team-mode-team-score">
                {teamScore}
              </span>
              <AdjBtn onClick={() => adjustGameScore('team', 1)} testid="team-mode-team-score-up">+</AdjBtn>
            </div>
          </div>
          <QuarterSelector />
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(0,0,0,0.4)' }} className="truncate max-w-[120px] ml-auto">
              {oppLabel}
            </div>
            <div className="flex items-center justify-end" style={{ gap: 5, marginTop: 2 }}>
              <AdjBtn onClick={() => adjustGameScore('opp', -1)} testid="team-mode-opp-score-down">−</AdjBtn>
              <span style={{ fontSize: 34, fontWeight: 800, color: 'rgba(0,0,0,0.25)', lineHeight: 1, letterSpacing: '-1px', minWidth: 36, textAlign: 'center' }} data-testid="team-mode-opp-score">
                {oppScore}
              </span>
              <AdjBtn onClick={() => adjustGameScore('opp', 1)} testid="team-mode-opp-score-up">+</AdjBtn>
            </div>
          </div>
        </div>

        {/* Pill grids */}
        <div style={{ padding: '10px 12px', background: '#F1F5F9', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <PillGrid side="home" headingLabel={teamLabel} openReboundOnMiss />
          {isBothTeams && (
            <PillGrid side="away" headingLabel={oppLabel} openReboundOnMiss />
          )}
        </div>

        {/* Footer — meta only. End Game lives in the top nav. */}
        <div className="flex items-center justify-between gap-2" style={{ padding: '6px 12px 8px', background: '#fff', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <span style={{ fontSize: 10, color: 'rgba(0,0,0,0.35)', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: '0.04em' }} data-testid="team-mode-footer-meta">
            TF: {homeFouls} · {periodLabel}
          </span>
        </div>
      </div>
    );
  };

  const MyPlayerScoringPanel = () => {
    const focusPlayer = roster.home.find(p => p.player_id === focusPlayerId);
    const playerName = focusPlayer?.player_name || 'My Player';
    const jersey = focusPlayer?.jersey_number != null ? String(focusPlayer.jersey_number) : '?';
    const initials = playerName.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

    // Aggregate focus-player events
    const fEvents = events.filter(e => e.player_id === focusPlayerId);
    const c = { m2:0, m3:0, mft:0, x2:0, x3:0, xft:0, ast:0, stl:0, blk:0, def:0, to:0, foul:0, oreb:0, dreb:0 };
    for (const e of fEvents) {
      if (e.event_type === 'shot_make') {
        if (e.sub_type === '2pt') c.m2++;
        else if (e.sub_type === '3pt') c.m3++;
        else if (e.sub_type === 'ft') c.mft++;
      } else if (e.event_type === 'shot_miss') {
        if (e.sub_type === '2pt') c.x2++;
        else if (e.sub_type === '3pt') c.x3++;
        else if (e.sub_type === 'ft') c.xft++;
      } else if (e.event_type === 'assist') c.ast++;
      else if (e.event_type === 'steal') c.stl++;
      else if (e.event_type === 'block') c.blk++;
      else if (e.event_type === 'deflection') c.def++;
      else if (e.event_type === 'turnover') c.to++;
      else if (e.event_type === 'foul') c.foul++;
      else if (e.event_type === 'rebound') {
        if (e.sub_type === 'offensive') c.oreb++;
        else if (e.sub_type === 'defensive') c.dreb++;
      }
    }
    const pts = c.m2 * 2 + c.m3 * 3 + c.mft;
    const fgMade = c.m2 + c.m3;
    const fgAtt = c.m2 + c.m3 + c.x2 + c.x3;
    const ftAtt = c.mft + c.xft;
    const pct = (made, total) => total === 0 ? '—' : `${Math.round(made / total * 100)}%`;
    const tsDenom = 2 * (fgAtt + 0.44 * ftAtt);
    const tsPct = tsDenom === 0 ? '—' : `${Math.round(pts / tsDenom * 100)}%`;
    const aTo = c.to === 0 ? (c.ast > 0 ? `${c.ast}:0` : '—') : (c.ast / c.to).toFixed(1);

    const teamScore = game.is_home ? game.home_score : game.away_score;
    const oppScore = game.is_home ? game.away_score : game.home_score;
    const teamLabel = (teamName || 'My Team').toUpperCase();
    const oppLabel = (game.opponent_name || 'OPPONENT').toUpperCase();

    const periodLabel = (() => {
      const fmt = game.period_format || 'quarters';
      const p = game.current_period || 1;
      if (fmt === 'halves') return p <= 2 ? `Half ${p}` : 'OT';
      return p <= 2 ? '1st Half' : (p <= 4 ? '2nd Half' : 'OT');
    })();

    const Pill = ({ variant, mainText, subText, onClick, testid }) => {
      const styles = {
        make:    { bg: '#e8963a', label: '#0B1824', sub: '#0B1824' },
        miss:    { bg: '#1e3a52', label: '#ffffff', sub: 'rgba(255,255,255,0.8)' },
        neutral: { bg: '#ffffff', label: '#0B1824', sub: '#0B1824', border: '1px solid rgba(0,0,0,0.1)' },
      }[variant];
      // Build 216: brand-orange capture pulse on every tap.
      const handleClick = (e) => {
        flashStatButton(e.currentTarget);
        onClick?.(e);
      };
      return (
        <button
          onClick={handleClick}
          data-testid={testid}
          className="rounded-full flex flex-col items-center justify-center select-none active:scale-95 transition-transform"
          style={{
            background: styles.bg,
            border: styles.border || 'none',
            fontFamily: "'Barlow Condensed', sans-serif",
            height: 60,
            padding: '4px',
          }}
        >
          {subText ? (
            <>
              <span style={{ fontSize: 22, fontWeight: 800, color: styles.label, lineHeight: 1, letterSpacing: '-0.01em' }}>
                {mainText}
              </span>
              <span style={{ fontSize: 9, fontWeight: 700, color: styles.sub, letterSpacing: '0.06em', marginTop: 3 }}>
                {subText}
              </span>
            </>
          ) : (
            <span style={{ fontSize: 17, fontWeight: 800, color: styles.label, lineHeight: 1, letterSpacing: '0.04em' }}>
              {mainText}
            </span>
          )}
        </button>
      );
    };

    const Tile = ({ value, label, accent, testid }) => (
      <div
        className="rounded-[10px] flex flex-col items-center justify-center text-center"
        style={{ background: '#F1F5F9', fontFamily: "'Barlow Condensed', sans-serif", height: 72, padding: '4px' }}
        data-testid={testid}
      >
        <span style={{ fontSize: 32, fontWeight: 800, lineHeight: 1, color: accent || '#0B1824', letterSpacing: '-0.02em' }}>{value}</span>
        <span style={{ fontSize: 9, fontWeight: 700, color: 'rgba(0,0,0,0.35)', letterSpacing: '0.06em', marginTop: 4 }}>{label}</span>
      </div>
    );

    const AdjBtn = ({ onClick, children, testid }) => (
      <button
        onClick={onClick}
        data-testid={testid}
        className="active:scale-90 transition-transform"
        style={{
          width: 26, height: 26, borderRadius: 6,
          border: '1px solid rgba(0,0,0,0.15)', background: '#f8fafc',
          color: 'rgba(0,0,0,0.4)', fontSize: 16, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: "'Barlow Condensed', sans-serif",
        }}
      >
        {children}
      </button>
    );

    return (
      <div
        className="rounded-2xl overflow-hidden"
        style={{ background: '#F1F5F9', fontFamily: "'Barlow Condensed', sans-serif", border: '1px solid rgba(0,0,0,0.08)' }}
        data-testid="my-player-scoring-panel"
      >
        {/* Score Header */}
        <div className="flex items-start justify-between" style={{ padding: '10px 16px', background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(0,0,0,0.4)' }}>{teamLabel}</div>
            <div className="flex items-center" style={{ gap: 5, marginTop: 2 }}>
              <AdjBtn onClick={() => adjustGameScore('team', -1)} testid="myp-team-score-down">−</AdjBtn>
              <span style={{ fontSize: 34, fontWeight: 800, color: '#0B1824', lineHeight: 1, letterSpacing: '-1px', minWidth: 36, textAlign: 'center' }} data-testid="myp-team-score">
                {teamScore}
              </span>
              <AdjBtn onClick={() => adjustGameScore('team', 1)} testid="myp-team-score-up">+</AdjBtn>
            </div>
          </div>
          <QuarterSelector />
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(0,0,0,0.4)' }} className="truncate max-w-[120px] ml-auto">
              {oppLabel}
            </div>
            <div className="flex items-center justify-end" style={{ gap: 5, marginTop: 2 }}>
              <AdjBtn onClick={() => adjustGameScore('opp', -1)} testid="myp-opp-score-down">−</AdjBtn>
              <span style={{ fontSize: 34, fontWeight: 800, color: 'rgba(0,0,0,0.25)', lineHeight: 1, letterSpacing: '-1px', minWidth: 36, textAlign: 'center' }} data-testid="myp-opp-score">
                {oppScore}
              </span>
              <AdjBtn onClick={() => adjustGameScore('opp', 1)} testid="myp-opp-score-up">+</AdjBtn>
            </div>
          </div>
        </div>

        {/* Player Hero. Build 219: 12-column box-score stat row.
            Columns (in order, all live-updating from the `c` counter):
              PTS · FGM-FGA · FG% · 3PM-3PA · FTM-FTA · FT% · REB ·
              AST · STL · BLK · TO · PF
            Wrapped in an `overflow-x-auto` container so the row
            scrolls horizontally on narrow viewports (12 cells still
            overflow a phone). Stat labels bumped from 9px → 11px
            for a more readable, less squashed footer beneath each
            value. */}
        <div className="flex items-center" style={{ padding: '8px 16px', background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.08)', gap: 12 }} data-testid="myp-player-hero">
          <div className="flex items-center shrink-0" style={{ gap: 10 }}>
            <div
              className="flex items-center justify-center"
              style={{ width: 32, height: 32, borderRadius: '50%', background: '#ffa133', color: '#fff', fontSize: 14, fontWeight: 800 }}
            >
              {jersey}
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#0B1824', textTransform: 'uppercase', letterSpacing: '0.02em' }} data-testid="myp-player-name">
                {playerName}
              </div>
              <div style={{ fontSize: 9, color: '#ffa133', fontWeight: 700, letterSpacing: '0.06em' }}>★ MY PLAYER</div>
            </div>
          </div>
          <div
            className="flex-1 min-w-0 overflow-x-auto"
            style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}
            data-testid="myp-hero-stats-scroll"
          >
            <div className="flex" style={{ gap: 14, textAlign: 'center' }}>
              {[
                { v: pts,                       lbl: 'PTS',     accent: '#ffa133', testid: 'myp-hero-pts' },
                { v: `${fgMade}-${fgAtt}`,      lbl: 'FGM-FGA',                    testid: 'myp-hero-fg' },
                { v: pct(fgMade, fgAtt),        lbl: 'FG%',                        testid: 'myp-hero-fgpct' },
                { v: `${c.m3}-${c.m3 + c.x3}`,  lbl: '3PM-3PA',                    testid: 'myp-hero-3p' },
                { v: `${c.mft}-${ftAtt}`,       lbl: 'FTM-FTA',                    testid: 'myp-hero-ft' },
                { v: pct(c.mft, ftAtt),         lbl: 'FT%',                        testid: 'myp-hero-ftpct' },
                { v: c.oreb + c.dreb,           lbl: 'REB',                        testid: 'myp-hero-reb' },
                { v: c.ast,                     lbl: 'AST',                        testid: 'myp-hero-ast' },
                { v: c.stl,                     lbl: 'STL',                        testid: 'myp-hero-stl' },
                { v: c.blk,                     lbl: 'BLK',                        testid: 'myp-hero-blk' },
                { v: c.to,                      lbl: 'TO',                         testid: 'myp-hero-to' },
                { v: c.foul,                    lbl: 'PF',                         testid: 'myp-hero-pf' },
              ].map(({ v, lbl, accent, testid }) => (
                <div key={lbl} data-testid={testid} style={{ minWidth: 38, flexShrink: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: accent || '#0B1824', whiteSpace: 'nowrap' }}>{v}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(0,0,0,0.35)', letterSpacing: '0.06em', marginTop: 1, whiteSpace: 'nowrap' }}>{lbl}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* TOP HALF — Pill grid */}
        <div style={{ padding: '8px 12px', background: '#F1F5F9', borderBottom: '2px solid rgba(0,0,0,0.08)' }} data-testid="myp-pill-grid">
          <div className="grid grid-cols-3" style={{ gap: 5, marginBottom: 5 }}>
            <Pill variant="make" mainText="+2"  subText="MAKE" testid="myp-pill-m2"  onClick={() => recordMyPlayerEvent('shot_make', '2pt', 2)} />
            <Pill variant="make" mainText="FT"  subText="MAKE" testid="myp-pill-mft" onClick={() => recordMyPlayerEvent('shot_make', 'ft', 1)} />
            <Pill variant="make" mainText="+3"  subText="MAKE" testid="myp-pill-m3"  onClick={() => recordMyPlayerEvent('shot_make', '3pt', 3)} />
          </div>
          <div className="grid grid-cols-3" style={{ gap: 5, marginBottom: 5 }}>
            <Pill variant="miss" mainText="+2" subText="MISS" testid="myp-pill-x2"  onClick={() => recordMyPlayerEvent('shot_miss', '2pt', 0)} />
            <Pill variant="miss" mainText="FT" subText="MISS" testid="myp-pill-xft" onClick={() => recordMyPlayerEvent('shot_miss', 'ft', 0)} />
            <Pill variant="miss" mainText="+3" subText="MISS" testid="myp-pill-x3"  onClick={() => recordMyPlayerEvent('shot_miss', '3pt', 0)} />
          </div>
          <div className="grid grid-cols-4" style={{ gap: 5, marginBottom: 5 }}>
            <Pill variant="neutral" mainText="AST"  testid="myp-pill-ast"  onClick={() => recordMyPlayerEvent('assist', null, 0)} />
            <Pill variant="neutral" mainText="STL"  testid="myp-pill-stl"  onClick={() => recordMyPlayerEvent('steal', null, 0)} />
            <Pill variant="neutral" mainText="OREB" testid="myp-pill-oreb" onClick={() => recordMyPlayerEvent('rebound', 'offensive', 0)} />
            <Pill variant="neutral" mainText="FOUL" testid="myp-pill-foul" onClick={() => recordMyPlayerEvent('foul', null, 0)} />
          </div>
          <div className="grid grid-cols-4" style={{ gap: 5 }}>
            <Pill variant="neutral" mainText="BLK"  testid="myp-pill-blk"  onClick={() => recordMyPlayerEvent('block', null, 0)} />
            <Pill variant="neutral" mainText="DEF"  testid="myp-pill-def"  onClick={() => recordMyPlayerEvent('deflection', null, 0)} />
            <Pill variant="neutral" mainText="DREB" testid="myp-pill-dreb" onClick={() => recordMyPlayerEvent('rebound', 'defensive', 0)} />
            <Pill variant="neutral" mainText="TO"   testid="myp-pill-to"   onClick={() => recordMyPlayerEvent('turnover', null, 0)} />
          </div>
        </div>

        {/* BOTTOM HALF — Stat tiles
            Build 220: row 1+2 are the shooting/efficiency metrics
            (unchanged). Rows 3–5 surface the raw counting stats the
            coach is actually tapping in the pill grid above —
            AST / STL / BLK / OREB / DREB / REB / TO / PF / DEF —
            so the same tiles view doubles as a live mini box score.
            Every cell reads from the `c` event counter that the
            pills mutate, so values update the moment a stat is
            recorded. */}
        <div style={{ padding: '8px 12px', background: '#fff' }} data-testid="myp-stat-tiles">
          <div className="grid grid-cols-3" style={{ gap: 5, marginBottom: 5 }}>
            <Tile value={pct(c.m2, c.m2 + c.x2)} label="2PT%" testid="myp-tile-2pt" />
            <Tile value={pct(c.m3, c.m3 + c.x3)} label="3PT%" testid="myp-tile-3pt" />
            <Tile value={pct(c.mft, ftAtt)}      label="FT%"  testid="myp-tile-ft"  />
          </div>
          <div className="grid grid-cols-3" style={{ gap: 5, marginBottom: 5 }}>
            <Tile value={pct(fgMade, fgAtt)} label="FG%"  testid="myp-tile-fg" />
            <Tile value={tsPct}              label="TS%"  testid="myp-tile-ts" />
            <Tile value={aTo}                label="A/TO" testid="myp-tile-ato" />
          </div>
          <div className="grid grid-cols-3" style={{ gap: 5, marginBottom: 5 }}>
            <Tile value={c.ast} label="AST" testid="myp-tile-ast" />
            <Tile value={c.stl} label="STL" testid="myp-tile-stl" />
            <Tile value={c.blk} label="BLK" testid="myp-tile-blk" />
          </div>
          <div className="grid grid-cols-3" style={{ gap: 5, marginBottom: 5 }}>
            <Tile value={c.oreb}            label="OREB" testid="myp-tile-oreb" />
            <Tile value={c.dreb}            label="DREB" testid="myp-tile-dreb" />
            <Tile value={c.oreb + c.dreb}   label="REB"  testid="myp-tile-reb" />
          </div>
          <div className="grid grid-cols-3" style={{ gap: 5 }}>
            <Tile value={c.to}   label="TO"  testid="myp-tile-to" />
            <Tile value={c.foul} label="PF"  testid="myp-tile-pf" />
            <Tile value={c.def}  label="DEF" testid="myp-tile-def" />
          </div>
        </div>

        {/* Footer — UNDO only for MY_PLAYER mode. End Game lives in top nav. */}
        <div className="flex items-center justify-between gap-2" style={{ padding: '6px 12px 8px', background: '#fff', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <span style={{ fontSize: 10, color: 'rgba(0,0,0,0.35)', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: '0.04em' }} data-testid="myp-footer-meta">
            TF: {c.foul} · {periodLabel}
          </span>
          <button
            onClick={handleUndo}
            data-testid="undo-button"
            className="active:scale-95 transition-transform flex items-center gap-1.5"
            style={{
              background: '#0B1824', color: '#fff', border: 'none',
              borderRadius: 50, padding: '7px 16px', fontSize: 12,
              fontWeight: 800, fontFamily: "'Barlow Condensed', sans-serif",
              letterSpacing: '0.08em', textTransform: 'uppercase',
            }}
          >
            <Undo className="w-3.5 h-3.5" />
            UNDO
          </button>
        </div>
      </div>
    );
  };

  // Determine team names - use actual team name instead of "Your Team"
  const homeTeamDisplayName = game.is_home ? (teamName || 'Home Team') : game.opponent_name;
  const awayTeamDisplayName = game.is_home ? game.opponent_name : (teamName || 'Away Team');
  const currentSide = pendingAction?.side;
  
  // Get ALL players for current side, sorted with: favorite first, on-court next, then bench
  const sortPlayersWithFocus = (list) => {
    if (!focusPlayerId) return list;
    const favorite = list.find(p => p.player_id === focusPlayerId);
    if (!favorite) return list;
    return [favorite, ...list.filter(p => p.player_id !== focusPlayerId)];
  };
  const allPlayersForSide = currentSide
    ? sortPlayersWithFocus([
        ...roster[currentSide].filter(p => p.on_court),
        ...roster[currentSide].filter(p => !p.on_court),
      ])
    : [];
  const onCourtCount = currentSide ? roster[currentSide].filter(p => p.on_court).length : 0;

  return (
    <div className="h-full overflow-y-auto bg-gradient-to-b from-white to-slate-50" style={{ WebkitOverflowScrolling: 'touch' }}>
      {/* Top Bar */}
      <div className="sticky top-0 z-40 shadow-sm" style={{ backgroundColor: '#13314b' }}>
        <div className="max-w-7xl mx-auto px-2 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center h-12 sm:h-14 gap-2">
            
            {/* Left: Back arrow — opens the exit sheet (Pause / Discard) */}
            <div className="flex-shrink-0">
              <Button
                onClick={() => setShowExitSheet(true)}
                variant="ghost"
                data-testid="back-button"
                className="text-white/70 hover:text-white hover:bg-white/10 h-9 w-9 sm:h-10 sm:w-10 p-0"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </div>

            {/* Divider */}
            <div className="h-6 w-px bg-white/15 flex-shrink-0" />

            {/* End Game button — direct save & finalize. Yellow pill so
                the action is always visible, never hidden behind the
                bottom nav or a scrolled-off footer. */}
            <button
              onClick={handleSaveAndExit}
              disabled={exiting}
              data-testid="end-game-nav-button"
              className="active:scale-[.97] transition-transform disabled:opacity-60 flex-shrink-0"
              style={{
                background: '#ffa133',
                color: '#0B1824',
                border: 'none',
                borderRadius: 50,
                padding: '7px 16px',
                fontSize: 13,
                fontWeight: 800,
                fontFamily: "'Barlow Condensed', sans-serif",
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              End Game
            </button>
          </div>
        </div>
      </div>

      <div
        className="max-w-7xl mx-auto p-2 sm:p-4 pb-app-nav"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 96px)' }}
      >
        {mode === 'MY_PLAYER' ? (
          /* ── MY PLAYER — solo, no tabs/log/roster, no action row.
                 UNDO + END GAME live in the panel footer. ── */
          <div data-testid="my-player-container">
            <MyPlayerScoringPanel />
          </div>
        ) : (
        <Tabs defaultValue="scoring" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-3 sm:mb-4 h-10 sm:h-12">
            <TabsTrigger value="scoring" data-testid="scoring-tab" className="font-bold uppercase text-xs sm:text-sm">Scoring</TabsTrigger>
            <TabsTrigger value="log" data-testid="log-tab" className="font-bold uppercase text-xs sm:text-sm">Game Log</TabsTrigger>
            <TabsTrigger value="roster" data-testid="roster-tab" className="font-bold uppercase text-xs sm:text-sm">Roster</TabsTrigger>
          </TabsList>

          {/* Action Buttons — unified navy pills. MY_TEAM = [UNDO, SUB];
              BOTH_TEAMS = [UNDO, SUB, OPP SUB]. Same height, same style. */}
          <div
            className={`grid ${mode === 'BOTH_TEAMS' ? 'grid-cols-3' : 'grid-cols-2'} gap-2 mb-3 sm:mb-4`}
            data-testid="action-row"
          >
            <button
              onClick={handleUndo}
              data-testid="undo-button"
              className="h-11 rounded-full text-white active:scale-[.98] transition-transform flex items-center justify-center gap-2"
              style={{
                backgroundColor: '#0B1824',
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 800,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                fontSize: 14,
              }}
            >
              <Undo className="w-4 h-4" />
              UNDO
            </button>
            <button
              onClick={() => handleOpenSubstitution('home')}
              data-testid="sub-home-button"
              className="h-11 rounded-full text-white active:scale-[.98] transition-transform flex items-center justify-center gap-2"
              style={{
                backgroundColor: '#0B1824',
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 800,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                fontSize: 14,
              }}
            >
              <RefreshCw className="w-4 h-4" />
              SUB
            </button>
            {mode === 'BOTH_TEAMS' && (
              <button
                onClick={() => handleOpenSubstitution('away')}
                data-testid="sub-away-button"
                className="h-11 rounded-full text-white active:scale-[.98] transition-transform flex items-center justify-center gap-2"
                style={{
                  backgroundColor: '#0B1824',
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 800,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  fontSize: 14,
                }}
              >
                <RefreshCw className="w-4 h-4" />
                OPP SUB
              </button>
            )}
          </div>

          <TabsContent value="scoring" className="space-y-3 sm:space-y-4">
            {focusPlayerId ? (
              <MyPlayerScoringPanel />
            ) : (
              <TeamScoringLayout />
            )}
          </TabsContent>

          <TabsContent value="log">
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-200 bg-slate-50">
                <h3 className="font-bold font-heading uppercase text-slate-900">Play by Play</h3>
              </div>
              <div className="divide-y divide-slate-200 max-h-[600px] overflow-y-auto">
                {events.length === 0 ? (
                  <p className="p-8 text-center text-slate-600">No events yet</p>
                ) : (
                  events.map((event) => {
                    // Period change events get special rendering
                    if (event.event_type === 'period_change') {
                      return (
                        <div key={event.event_id} className="p-3 text-sm text-center" style={{ backgroundColor: '#f1f5f9' }}>
                          <span className="font-bold uppercase text-slate-500">{event.description || `Period changed to ${event.period}`}</span>
                        </div>
                      );
                    }
                    
                    const playerName = getPlayerName(event.player_id, event.side);
                    const teamDisplayName = event.side === 'home' ? homeTeamDisplayName : awayTeamDisplayName;
                    const displayName = playerName || teamDisplayName;
                    
                    return (
                      <div key={event.event_id} className="p-3 hover:bg-slate-50 text-sm">
                        <span className="font-bold text-slate-900">Q{event.period}</span>
                        {' • '}
                        <span className="text-blue-700 font-semibold">{displayName}</span>
                        {' • '}
                        <span className="text-slate-600">{event.event_type.replace('_', ' ').toUpperCase()}</span>
                        {event.sub_type && ` (${event.sub_type})`}
                        {event.points > 0 && <span className="text-green-600 font-bold"> +{event.points}</span>}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="roster">
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold font-heading uppercase text-slate-900">{homeTeamDisplayName} Roster</h3>
                  <span className={`text-sm font-bold px-2 py-1 rounded ${getTeamFoulsForHalf('home') >= 7 ? 'bg-red-100 text-red-700' : getTeamFoulsForHalf('home') >= 5 ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-600'}`}>
                    Team Fouls: {getTeamFoulsForHalf('home')}
                  </span>
                </div>
                <div className="space-y-2">
                  {roster.home.length === 0 ? (
                    <p className="text-slate-600 text-sm">Team stats only mode</p>
                  ) : (
                    roster.home.map((player) => {
                      const fouls = getPlayerFouls(player.player_id, 'home');
                      const foulColorClass = getFoulColorClass(fouls);
                      return (
                        <div key={player.player_id} className="flex items-center justify-between p-3 bg-slate-50 rounded">
                          <div className="flex items-center gap-3">
                            <span className="font-medium">#{player.jersey_number} {player.player_name}</span>
                            {fouls > 0 && (
                              <span className={`text-xs font-bold px-2 py-1 rounded ${foulColorClass}`}>
                                {fouls} {fouls === 1 ? 'Foul' : 'Fouls'}
                              </span>
                            )}
                          </div>
                          <Button
                            onClick={() => toggleOnCourt(player.roster_id, player.on_court)}
                            size="sm"
                            className={`h-8 px-3 font-bold text-xs ${
                              player.on_court
                                ? 'text-white'
                                : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                            }`}
                            style={player.on_court ? { backgroundColor: '#17a31c' } : {}}
                          >
                            {player.on_court ? 'ON COURT' : 'BENCH'}
                          </Button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold font-heading uppercase text-slate-900">{awayTeamDisplayName} Roster</h3>
                  <span className={`text-sm font-bold px-2 py-1 rounded ${getTeamFoulsForHalf('away') >= 7 ? 'bg-red-100 text-red-700' : getTeamFoulsForHalf('away') >= 5 ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-600'}`}>
                    Team Fouls: {getTeamFoulsForHalf('away')}
                  </span>
                </div>
                <div className="space-y-2">
                  {roster.away.length === 0 ? (
                    <p className="text-slate-600 text-sm">Team stats only mode</p>
                  ) : (
                    roster.away.map((player) => {
                      const fouls = getPlayerFouls(player.player_id, 'away');
                      const foulColorClass = getFoulColorClass(fouls);
                      return (
                        <div key={player.player_id} className="flex items-center justify-between p-3 bg-slate-50 rounded">
                          <div className="flex items-center gap-3">
                            <span className="font-medium">#{player.jersey_number} {player.player_name}</span>
                            {fouls > 0 && (
                              <span className={`text-xs font-bold px-2 py-1 rounded ${foulColorClass}`}>
                                {fouls} {fouls === 1 ? 'Foul' : 'Fouls'}
                              </span>
                            )}
                          </div>
                          <Button
                            onClick={() => toggleOnCourt(player.roster_id, player.on_court)}
                            size="sm"
                            className={`h-8 px-3 font-bold text-xs ${
                              player.on_court
                                ? 'text-white'
                                : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                            }`}
                            style={player.on_court ? { backgroundColor: '#17a31c' } : {}}
                          >
                            {player.on_court ? 'ON COURT' : 'BENCH'}
                          </Button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
        )}
      </div>

      {/* Athlete Selection Modal - Shows ALL rostered players with on-court at top */}
      <Dialog open={showAthleteModal} onOpenChange={setShowAthleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-2xl">{getModalTitle()}</DialogTitle>
            <p className="text-sm text-slate-500">
              {onCourtCount > 0 ? `${onCourtCount} players on court (shown first)` : 'Select a player'}
            </p>
          </DialogHeader>
          <div className="space-y-2 py-4 max-h-[500px] overflow-y-auto">
            {/* Favorite "hero" card (when wizard set focusPlayerId) */}
            {focusPlayerId && (() => {
              const fav = allPlayersForSide.find(p => p.player_id === focusPlayerId);
              if (!fav) return null;
              return (
                <div className="mb-4">
                  <p className="text-xs font-bold uppercase mb-2 px-1 flex items-center gap-1.5"
                     style={{ color: '#ffa133' }}>
                    ★ Featured Player
                  </p>
                  <button
                    key={`hero-${fav.player_id}`}
                    onClick={() => {
                      if (ftAttempts.length > 0) {
                        handleFTAthleteSelected(fav.player_id, fav.player_name, fav.jersey_number);
                      } else {
                        handleAthleteSelected(fav.player_id, fav.player_name, fav.jersey_number);
                      }
                    }}
                    data-testid={`athlete-hero-${fav.player_id}`}
                    className="w-full p-5 mb-2 rounded-xl transition-all text-left active:scale-[.98]"
                    style={{
                      background: 'rgba(255,161,51,0.10)',
                      border: '2px solid #ffa133',
                      minHeight: '76px',
                    }}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
                           style={{ background: '#ffa133' }}>
                        <span className="text-white font-bold text-2xl">{fav.jersey_number || '#'}</span>
                      </div>
                      <div>
                        <span className="font-bold text-xl text-slate-900">{fav.player_name}</span>
                        <div className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#ffa133' }}>
                          {fav.on_court ? 'On Court · Favorite' : 'Bench · Favorite'}
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              );
            })()}

            {/* On-court players section (excluding the favorite if shown above) */}
            {allPlayersForSide.filter(p => p.on_court && p.player_id !== focusPlayerId).length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-bold uppercase text-green-600 mb-2 px-1">On Court</p>
                {allPlayersForSide.filter(p => p.on_court && p.player_id !== focusPlayerId).map((athlete) => (
                  <button
                    key={athlete.player_id}
                    onClick={() => {
                      if (ftAttempts.length > 0) {
                        handleFTAthleteSelected(athlete.player_id, athlete.player_name, athlete.jersey_number);
                      } else {
                        handleAthleteSelected(athlete.player_id, athlete.player_name, athlete.jersey_number);
                      }
                    }}
                    data-testid={`athlete-${athlete.player_id}`}
                    className="w-full p-4 mb-2 bg-green-50 hover:bg-green-100 border-2 border-green-300 hover:border-green-500 rounded-lg transition-all text-left"
                    style={{ minHeight: '60px' }}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-xl">{athlete.jersey_number || '#'}</span>
                      </div>
                      <div>
                        <span className="font-bold text-lg text-slate-900">{athlete.player_name}</span>
                        <span className="ml-2 text-xs text-green-600 font-bold">ON COURT</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Bench players section (excluding the favorite if shown above) */}
            {allPlayersForSide.filter(p => !p.on_court && p.player_id !== focusPlayerId).length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase text-slate-400 mb-2 px-1">Bench</p>
                {allPlayersForSide.filter(p => !p.on_court && p.player_id !== focusPlayerId).map((athlete) => (
                  <button
                    key={athlete.player_id}
                    onClick={() => {
                      if (ftAttempts.length > 0) {
                        handleFTAthleteSelected(athlete.player_id, athlete.player_name, athlete.jersey_number);
                      } else {
                        handleAthleteSelected(athlete.player_id, athlete.player_name, athlete.jersey_number);
                      }
                    }}
                    data-testid={`athlete-${athlete.player_id}`}
                    className="w-full p-4 mb-2 bg-slate-50 hover:bg-blue-50 border-2 border-slate-200 hover:border-blue-500 rounded-lg transition-all text-left"
                    style={{ minHeight: '60px' }}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-slate-400 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-xl">{athlete.jersey_number || '#'}</span>
                      </div>
                      <div>
                        <span className="font-bold text-lg text-slate-900">{athlete.player_name}</span>
                        <span className="ml-2 text-xs text-slate-400 font-bold">BENCH</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* No players - show team option */}
            {allPlayersForSide.length === 0 && (
              <div className="text-center py-4">
                <p className="text-slate-500 mb-4">No players on roster</p>
                <Button
                  onClick={() => {
                    setShowAthleteModal(false);
                    const action = pendingAction;
                    if (ftAttempts.length > 0) {
                      handleFTTeamRecord(ftAttempts);
                    } else {
                      recordTeamStat(action.side, action.eventType, action.subType, action.points);
                    }
                  }}
                  className="bg-slate-600 hover:bg-slate-700"
                >
                  Record as Team Stat
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* FT Setup Modal */}
      <Dialog open={showFTSetupModal} onOpenChange={setShowFTSetupModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-2xl">Free Throw Attempt</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <Button
              onClick={() => handleFTTypeSelected('1AND1')}
              className="w-full h-16 text-white font-bold text-lg uppercase hover:opacity-90"
              style={{ backgroundColor: '#13314b' }}
            >
              1-and-1
            </Button>
            <Button
              onClick={() => handleFTTypeSelected('2FT')}
              className="w-full h-16 text-white font-bold text-lg uppercase hover:opacity-90"
              style={{ backgroundColor: '#13314b' }}
            >
              2 FT
            </Button>
            <Button
              onClick={() => handleFTTypeSelected('3FT')}
              className="w-full h-16 text-white font-bold text-lg uppercase hover:opacity-90"
              style={{ backgroundColor: '#13314b' }}
            >
              3 FT
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* FT Recording Modal */}
      <Dialog open={showFTRecordModal} onOpenChange={setShowFTRecordModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-2xl">Record Free Throws</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-4">
              {ftAttempts.map((attempt, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded">
                  <span className="font-bold">FT {idx + 1}:</span>
                  <span className={`font-bold ${attempt.isMake ? 'text-green-600' : 'text-red-600'}`}>
                    {attempt.isMake ? 'MAKE' : 'MISS'}
                  </span>
                </div>
              ))}
              
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
                <span className="font-bold text-lg">FT {currentFTIndex + 1}:</span>
                <div className="flex space-x-3">
                  <Button
                    onClick={() => handleFTAttempt(true)}
                    className="text-white font-bold px-6 h-12 hover:opacity-90"
                    style={{ backgroundColor: '#16A085' }}
                  >
                    MAKE
                  </Button>
                  <Button
                    onClick={() => handleFTAttempt(false)}
                    className="text-white font-bold px-6 h-12 hover:opacity-90"
                    style={{ backgroundColor: '#C0392B' }}
                  >
                    MISS
                  </Button>
                </div>
              </div>

              {/* Substitution button during Free Throws */}
              <Button
                onClick={() => {
                  setShowFTRecordModal(false);
                  handleOpenSubstitution(pendingAction?.side || 'home');
                }}
                variant="outline"
                data-testid="ft-substitution-button"
                className="w-full h-12 font-bold uppercase border-2 border-purple-300 text-purple-700 hover:bg-purple-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Make Substitution
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rebound modal — drops from the top, mirrors the rest of the
          app's modal language. Three options:
            - MY TEAM    → opens player picker (with an Unassigned option
                           that posts a team rebound, no player_id)
            - OPPONENT   → records a team rebound on the away side
            - OUT OF BOUNDS → cancels (no event recorded) */}
      <Dialog
        open={showReboundModal}
        onOpenChange={(o) => {
          if (!o) { setShowReboundModal(false); setReboundView('options'); setPendingRebound(null); }
        }}
      >
        <DialogContent className="max-w-md" data-testid="rebound-modal">
          {reboundView === 'options' ? (
            <>
              <DialogHeader>
                <DialogTitle
                  className="text-lg font-bold uppercase tracking-tight"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Rebound?
                </DialogTitle>
                <p className="text-[11px] uppercase tracking-[.06em]"
                  style={{ color: 'rgba(0,0,0,0.45)', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700 }}>
                  Who got the board
                </p>
              </DialogHeader>
              <div className="mt-2 flex flex-col gap-2.5">
                <button
                  onClick={() => setReboundView('players')}
                  data-testid="rebound-option-my-team"
                  className="active:scale-[.98] transition-transform"
                  style={{
                    height: 60, borderRadius: 50, background: '#e8963a', border: 'none',
                    fontSize: 19, fontWeight: 800, color: '#0B1824',
                    letterSpacing: '0.04em', fontFamily: "'Barlow Condensed', sans-serif",
                  }}
                >
                  MY TEAM
                </button>
                <button
                  onClick={async () => {
                    const missingSide = pendingRebound?.side;
                    // Opponent grabbed it — sub_type is from the opponent's
                    // perspective: their offensive board if they missed,
                    // their defensive board if our team missed.
                    const subType = missingSide === 'home' ? 'defensive' : 'offensive';
                    await createEvent({
                      period: game.current_period,
                      game_clock_time: '12:00',
                      side: 'away',
                      team_id: game.team_id,
                      player_id: null,
                      event_type: 'rebound',
                      sub_type: subType,
                      points: 0,
                    });
                    setShowReboundModal(false); setReboundView('options'); setPendingRebound(null);
                  }}
                  data-testid="rebound-option-opponent"
                  className="active:scale-[.98] transition-transform"
                  style={{
                    height: 60, borderRadius: 50, background: '#1e3a52', border: 'none',
                    fontSize: 19, fontWeight: 800, color: '#fff',
                    letterSpacing: '0.04em', fontFamily: "'Barlow Condensed', sans-serif",
                  }}
                >
                  OPPONENT
                </button>
                <button
                  onClick={() => { setShowReboundModal(false); setReboundView('options'); setPendingRebound(null); }}
                  data-testid="rebound-option-oob"
                  className="active:scale-[.98] transition-transform"
                  style={{
                    height: 56, borderRadius: 50, background: '#fff',
                    border: '1px solid rgba(0,0,0,0.15)',
                    fontSize: pendingRebound?.missType === 'ft' ? 15 : 17, fontWeight: 800, color: '#0B1824',
                    letterSpacing: '0.04em', fontFamily: "'Barlow Condensed', sans-serif",
                  }}
                >
                  {pendingRebound?.missType === 'ft' ? 'OUT OF BOUNDS / NO REBOUND' : 'OUT OF BOUNDS'}
                </button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setReboundView('options')}
                    data-testid="rebound-back-button"
                    style={{ fontSize: 12, fontWeight: 700, color: 'rgba(0,0,0,0.5)', letterSpacing: '0.04em', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}
                  >
                    ← BACK
                  </button>
                  <DialogTitle
                    className="text-lg font-bold uppercase tracking-tight"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    Pick Player
                  </DialogTitle>
                  <div style={{ width: 40 }} />
                </div>
              </DialogHeader>
              <div className="mt-2 flex flex-col gap-1.5 max-h-[55vh] overflow-y-auto">
                {/* Unassigned — credit the rebound to the team total
                    without attributing it to any individual player. */}
                <button
                  onClick={async () => {
                    const missingSide = pendingRebound?.side;
                    const subType = missingSide === 'home' ? 'offensive' : 'defensive';
                    await createEvent({
                      period: game.current_period,
                      game_clock_time: '12:00',
                      side: 'home',
                      team_id: game.team_id,
                      player_id: null,
                      event_type: 'rebound',
                      sub_type: subType,
                      points: 0,
                    });
                    setShowReboundModal(false); setReboundView('options'); setPendingRebound(null);
                  }}
                  data-testid="rebound-player-unassigned"
                  className="active:scale-[.99] transition-transform"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', borderRadius: 12,
                    background: '#FFF7EC',
                    border: '1px solid #e8963a',
                    fontFamily: "'Barlow Condensed', sans-serif",
                    cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#e8963a', color: '#0B1824', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>
                    TM
                  </div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#0B1824', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                      Unassigned
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.5)', fontWeight: 700, letterSpacing: '.06em' }}>
                      ADD TO TEAM TOTAL
                    </div>
                  </div>
                </button>

                {(roster.home || []).length === 0 ? null : (
                  (roster.home || []).map(p => (
                    <button
                      key={p.player_id}
                      onClick={async () => {
                        const missingSide = pendingRebound?.side;
                        const subType = missingSide === 'home' ? 'offensive' : 'defensive';
                        await createEvent({
                          period: game.current_period,
                          game_clock_time: '12:00',
                          side: 'home',
                          team_id: game.team_id,
                          player_id: p.player_id,
                          event_type: 'rebound',
                          sub_type: subType,
                          points: 0,
                        });
                        setShowReboundModal(false); setReboundView('options'); setPendingRebound(null);
                      }}
                      data-testid={`rebound-player-${p.player_id}`}
                      className="active:scale-[.99] transition-transform"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 14px', borderRadius: 12,
                        background: '#F1F5F9', border: '1px solid rgba(0,0,0,0.06)',
                        fontFamily: "'Barlow Condensed', sans-serif",
                        cursor: 'pointer', textAlign: 'left',
                      }}
                    >
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#0B1824', color: '#fff', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>
                        #{p.jersey_number ?? '?'}
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#0B1824', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                        {p.player_name}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* BOTH_TEAMS home-side stat attribution modal — drops from the
          top. Captures the player who completed the stat before the
          event is recorded so it lands in the box score with the
          correct player_id. For misses, the rebound modal opens
          immediately after attribution. */}
      <Dialog
        open={showAttributeModal}
        onOpenChange={(o) => {
          if (!o) { setShowAttributeModal(false); setPendingAttribution(null); }
        }}
      >
        <DialogContent className="max-w-md" data-testid="attribute-modal">
          <DialogHeader>
            <DialogTitle
              className="text-lg font-bold uppercase tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              {pendingAttribution?.eventType === 'shot_make'
                ? 'Who made it?'
                : pendingAttribution?.eventType === 'shot_miss'
                  ? 'Who took the shot?'
                  : 'Who completed the stat?'}
            </DialogTitle>
            <p className="text-[11px] uppercase tracking-[.06em]"
              style={{ color: 'rgba(0,0,0,0.45)', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700 }}>
              {pendingAttribution
                ? `${(pendingAttribution.eventType || '').replace('_', ' ')}${pendingAttribution.subType ? ` · ${pendingAttribution.subType}` : ''}`
                : ''}
            </p>
          </DialogHeader>
          <div className="mt-2 flex flex-col gap-1.5 max-h-[55vh] overflow-y-auto">
            {/* Unassigned — credit the stat to the team total without
                attributing it to any individual player. Misses still
                chain into the rebound modal afterward. */}
            <button
              onClick={() => completeHomeAttribution(null)}
              data-testid="attribute-player-unassigned"
              className="active:scale-[.99] transition-transform"
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 14px', borderRadius: 12,
                background: '#FFF7EC',
                border: '1px solid #e8963a',
                fontFamily: "'Barlow Condensed', sans-serif",
                cursor: 'pointer', textAlign: 'left',
              }}
            >
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#e8963a', color: '#0B1824', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>
                TM
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#0B1824', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                  Unassigned
                </div>
                <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.5)', fontWeight: 700, letterSpacing: '.06em' }}>
                  ADD TO TEAM TOTAL
                </div>
              </div>
            </button>

            {(roster.home || []).length === 0 ? (
              <div className="py-4 text-center" style={{ color: 'rgba(0,0,0,0.4)', fontSize: 14 }}>
                No players on roster.
              </div>
            ) : (
              (roster.home || []).map(p => (
                <button
                  key={p.player_id}
                  onClick={() => completeHomeAttribution(p.player_id)}
                  data-testid={`attribute-player-${p.player_id}`}
                  className="active:scale-[.99] transition-transform"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', borderRadius: 12,
                    background: '#F1F5F9', border: '1px solid rgba(0,0,0,0.06)',
                    fontFamily: "'Barlow Condensed', sans-serif",
                    cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#0B1824', color: '#fff', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>
                    #{p.jersey_number ?? '?'}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#0B1824', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                    {p.player_name}
                  </div>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assist Selection Modal - Shows after a made shot */}
      <Dialog open={showAssistModal} onOpenChange={setShowAssistModal}>
        <DialogContent className="max-w-md max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-2xl">Who got the assist?</DialogTitle>
            <p className="text-sm text-slate-500">
              {pendingShooter && `After #${pendingShooter.jerseyNumber} ${pendingShooter.athleteName}'s made shot`}
            </p>
          </DialogHeader>
          <div className="space-y-3 py-4 max-h-[60vh] overflow-y-auto">
            {/* No Assist Option - First */}
            <button
              onClick={() => handleAssistSelected('no_assist')}
              data-testid="assist-none"
              className="w-full p-4 bg-slate-100 hover:bg-slate-200 border-2 border-slate-300 hover:border-slate-400 rounded-lg transition-all text-center"
            >
              <span className="font-bold text-slate-700">No Assist</span>
              <p className="text-xs text-slate-500 mt-1">Unassisted shot</p>
            </button>

            <div className="border-t border-slate-200 my-3"></div>

            {/* Players from the same team */}
            {(() => {
              const assistSide = pendingShooter?.side;
              const teamPlayers = roster[assistSide] || [];
              const shooterId = pendingShooter?.athleteId;
              // Filter out the shooter from assist options
              const availablePlayers = teamPlayers.filter(p => p.player_id !== shooterId);
              
              return (
                <>
                  {/* On Court Players */}
                  {availablePlayers.filter(p => p.on_court).length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-bold uppercase text-blue-600 mb-2 px-1">On Court</p>
                      {availablePlayers.filter(p => p.on_court).map((athlete) => (
                        <button
                          key={athlete.player_id}
                          onClick={() => handleAssistSelected(athlete.player_id, athlete.player_name, athlete.jersey_number)}
                          data-testid={`assist-${athlete.player_id}`}
                          className="w-full p-3 mb-2 bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 hover:border-blue-400 rounded-lg transition-all text-left"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white font-bold">{athlete.jersey_number || '#'}</span>
                            </div>
                            <span className="font-bold text-slate-900">{athlete.player_name}</span>
                            <span className="text-xs text-blue-600 font-bold">ON COURT</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Bench Players */}
                  {availablePlayers.filter(p => !p.on_court).length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase text-slate-400 mb-2 px-1">Bench</p>
                      {availablePlayers.filter(p => !p.on_court).map((athlete) => (
                        <button
                          key={athlete.player_id}
                          onClick={() => handleAssistSelected(athlete.player_id, athlete.player_name, athlete.jersey_number)}
                          data-testid={`assist-bench-${athlete.player_id}`}
                          className="w-full p-3 mb-2 bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-400 rounded-lg transition-all text-left"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-slate-400 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white font-bold">{athlete.jersey_number || '#'}</span>
                            </div>
                            <span className="font-medium text-slate-700">{athlete.player_name}</span>
                            <span className="text-xs text-slate-400">BENCH</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {availablePlayers.length === 0 && (
                    <p className="text-center text-slate-500 py-4">No other players on roster</p>
                  )}
                </>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Substitution Modal */}
      <Dialog open={showSubstitutionModal} onOpenChange={setShowSubstitutionModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-2xl">
              {substitutionSide === 'home' ? homeTeamDisplayName : awayTeamDisplayName} Substitution
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* On Court Players */}
            <div>
              <h4 className="font-bold uppercase text-sm text-slate-600 mb-2">On Court ({roster[substitutionSide]?.filter(p => p.on_court).length || 0}/5)</h4>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {roster[substitutionSide]?.filter(p => p.on_court).map((player) => {
                  const fouls = getPlayerFouls(player.player_id, substitutionSide);
                  const foulColorClass = getFoulColorClass(fouls);
                  return (
                    <div key={player.player_id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold">{player.jersey_number || '#'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{player.player_name}</span>
                          {fouls > 0 && (
                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${foulColorClass}`}>
                              {fouls}F
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        onClick={() => {
                          handleSubstitution(null, player);
                        }}
                        size="sm"
                        className="text-white font-bold text-xs h-8 px-3 hover:opacity-90"
                        style={{ backgroundColor: '#b61229' }}
                      >
                        Sub Out
                      </Button>
                    </div>
                  );
                })}
                {roster[substitutionSide]?.filter(p => p.on_court).length === 0 && (
                  <p className="text-slate-500 text-sm p-3">No players on court</p>
                )}
              </div>
            </div>

            {/* Bench Players */}
            <div>
              <h4 className="font-bold uppercase text-sm text-slate-600 mb-2">Bench</h4>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {roster[substitutionSide]?.filter(p => !p.on_court).map((player) => {
                  const fouls = getPlayerFouls(player.player_id, substitutionSide);
                  const foulColorClass = getFoulColorClass(fouls);
                  return (
                    <div key={player.player_id} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-slate-400 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold">{player.jersey_number || '#'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{player.player_name}</span>
                          {fouls > 0 && (
                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${foulColorClass}`}>
                              {fouls}F
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        onClick={() => {
                          handleSubstitution(player, null);
                        }}
                        size="sm"
                        className="text-white font-bold text-xs h-8 px-3 hover:opacity-90"
                        style={{ backgroundColor: '#17a31c' }}
                      >
                        Sub In
                      </Button>
                    </div>
                  );
                })}
                {roster[substitutionSide]?.filter(p => !p.on_court).length === 0 && (
                  <p className="text-slate-500 text-sm p-3">No players on bench</p>
                )}
              </div>
            </div>

            {/* Add Player Button */}
            <Button
              onClick={() => setShowAddPlayerModal(true)}
              variant="outline"
              data-testid="add-player-button"
              className="w-full h-12 font-bold uppercase border-2 border-dashed border-slate-300 text-slate-600 hover:bg-slate-50"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Player
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Player Modal */}
      <Dialog open={showAddPlayerModal} onOpenChange={setShowAddPlayerModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-2xl">Add Player to Roster</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                Player Name *
              </label>
              <Input
                value={newPlayerName}
                onChange={(e) => setNewPlayerName(e.target.value)}
                placeholder="Enter player name"
                className="h-12"
                data-testid="new-player-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                Jersey Number
              </label>
              <Input
                value={newPlayerJersey}
                onChange={(e) => setNewPlayerJersey(e.target.value)}
                placeholder="e.g., 23"
                className="h-12"
                data-testid="new-player-jersey-input"
              />
            </div>
            <div className="flex space-x-3 pt-2">
              <Button
                onClick={() => {
                  setShowAddPlayerModal(false);
                  setNewPlayerName('');
                  setNewPlayerJersey('');
                }}
                variant="outline"
                className="flex-1 h-12"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddPlayerToRoster}
                data-testid="confirm-add-player-button"
                className="flex-1 h-12 text-white font-bold hover:opacity-90"
                style={{ backgroundColor: '#13314b' }}
              >
                Add Player
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Exit-game bottom sheet — Save & Exit / Discard & Exit */}
      <Sheet open={showExitSheet} onOpenChange={(open) => !exiting && setShowExitSheet(open)}>
        <SheetContent
          side="bottom"
          data-testid="exit-game-sheet"
          className="rounded-t-2xl border-0 p-0 max-h-[80vh]"
          style={{ background: '#fff' }}
        >
          <div className="px-5 pt-5 pb-6">
            <div
              className="mx-auto mb-4 h-1.5 w-10 rounded-full"
              style={{ background: 'rgba(0,0,0,0.12)' }}
            />
            <h3
              className="text-center"
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: 20,
                fontWeight: 800,
                color: '#0B1824',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              Leave this game?
            </h3>
            <p className="mt-2 text-center text-sm text-slate-500">
              Choose how you want to leave.
            </p>

            <div className="mt-6 flex flex-col gap-3">
              <button
                onClick={handlePauseAndExit}
                disabled={exiting}
                data-testid="exit-pause-button"
                className="h-12 rounded-full text-white active:scale-[.98] transition-transform disabled:opacity-60"
                style={{
                  background: '#e8963a',
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  fontSize: 15,
                }}
              >
                Pause
              </button>
              <button
                onClick={handleDiscardAndExit}
                disabled={exiting}
                data-testid="exit-discard-button"
                className="h-12 rounded-full active:scale-[.98] transition-transform disabled:opacity-60"
                style={{
                  background: '#fff',
                  color: '#B91C1C',
                  border: '1.5px solid #FCA5A5',
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  fontSize: 15,
                }}
              >
                Discard &amp; Exit
              </button>
              <button
                onClick={() => !exiting && setShowExitSheet(false)}
                disabled={exiting}
                data-testid="exit-cancel-button"
                className="h-11 rounded-full active:scale-[.98] transition-transform disabled:opacity-60"
                style={{
                  background: '#F1F5F9',
                  color: '#475569',
                  border: 'none',
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  fontSize: 13,
                }}
              >
                Keep Tracking
              </button>
            </div>

            <p className="mt-4 text-center text-[11px] text-slate-400">
              Pause keeps the game live so you can resume from Games. Tap End Game in the top nav to finalize. Discard &amp; Exit permanently deletes this game.
            </p>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
